import { Button, Group, Select } from '@mantine/core';
import { IconFilter, IconPlus } from '@tabler/icons-react';
import { SearchInput } from '../../../components/SearchInput';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { type Practice, ROLE_LABELS } from '../types/rbac';

interface UserFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  practices?: Practice[];
  practiceFilter?: string[];
  onPracticeChange?: (value: string[]) => void;
  roleFilter: string[];
  onRoleChange: (value: string[]) => void;
  statusFilter: string | null;
  onStatusChange: (value: string | null) => void;
  /** When false, hide the practice dropdown (e.g. when scoped to a single practice). Default true. */
  showPracticeFilter?: boolean;
  /** When true, hide the All Roles filter (e.g. Clients tab). */
  hideRoles?: boolean;
  onCreateUser: () => void;
  createButtonLabel?: string;
  /** When set, shows a second button for the full client intake wizard. */
  onCreateClient?: () => void;
  /** Hide the primary create button (e.g. clients tab only shows Add Client). */
  hideCreateUserButton?: boolean;
}

export function UserFilters({
  searchQuery,
  onSearchChange,
  practices = [],
  practiceFilter = [],
  onPracticeChange,
  roleFilter = [],
  onRoleChange,
  statusFilter,
  onStatusChange,
  showPracticeFilter = true,
  hideRoles = false,
  onCreateUser,
  createButtonLabel = 'Create User',
  onCreateClient,
  hideCreateUserButton = false,
}: UserFiltersProps): JSX.Element {
  const practiceOptions = practices.map((p) => ({ value: p.id, label: p.name }));
  const roleOptions = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Group gap="md" align="center" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
        <SearchInput
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ flex: '1 1 12rem', minWidth: 160, maxWidth: 280 }}
        />
        {showPracticeFilter && (
          <FilterMultiSelect
            allLabel="All Practices"
            leftSection={<IconFilter size={14} color="var(--mantine-color-gray-5)" />}
            data={practiceOptions}
            value={practiceFilter}
            onChange={onPracticeChange ?? (() => { })}
            w={220}
            style={{ minWidth: 180 }}
          />
        )}
        {!hideRoles ? (
          <FilterMultiSelect
            allLabel="All Roles"
            leftSection={<IconFilter size={14} color="var(--mantine-color-gray-5)" />}
            data={roleOptions}
            value={roleFilter}
            onChange={onRoleChange}
            w={180}
            style={{ minWidth: 160 }}
          />
        ) : null}
        <Select
          placeholder="All Statuses"
          leftSection={<IconFilter size={14} color="var(--mantine-color-gray-5)" />}
          data={[
            { value: '', label: 'All Statuses' },
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
            { value: 'Pending', label: 'Pending' },
          ]}
          value={statusFilter ?? ''}
          onChange={onStatusChange}
          w={180}
          miw={140}
        />
      </Group>
      <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
        {!hideCreateUserButton ? (
          <Button leftSection={<IconPlus size={16} />} onClick={onCreateUser}>
            {createButtonLabel}
          </Button>
        ) : null}
        {onCreateClient ? (
          <Button variant="light" leftSection={<IconPlus size={16} />} onClick={onCreateClient}>
            Add Client
          </Button>
        ) : null}
      </Group>
    </Group>
  );
}
